import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { loadFirebase } from '../firebase';
import { INITIAL_WORKSHOPS, DEFAULT_ADMIN_SETTINGS } from '../data/workshops';
import { captureAttribution, attributionForRecord, randomId } from '../lib/attribution';
import {
  initPixel,
  trackPageView,
  trackViewContent,
  trackLead,
  trackInitiateCheckout,
  trackPurchase,
} from '../lib/tracking';
import { fetchPaymentStatus, getJson, requestLoginOtp, verifyLoginOtp } from '../lib/api';
import { priceBreakdown, formatDateTime } from '../lib/format';

const AppContext = createContext(null);

/** Hard ceiling for testimonial videos. Mirrors `storage.rules` — rejecting
 *  here means the user finds out before sitting through a long upload that the
 *  bucket was always going to refuse. */
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;

/* ── Local persistence ─────────────────────────────────────────────────────
   Firestore is the source of truth, but a visitor who arrives from an ad
   should never see an empty page because the SDK is still downloading.
   Everything is mirrored to localStorage and read back synchronously on boot. */

const readStore = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeStore = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Storage full or disabled — in-memory state still works. */
  }
};

/** Firestore writes are best-effort from the browser; never break the flow. */
async function tryWrite(operation, context) {
  try {
    await operation();
    return true;
  } catch (error) {
    console.warn(`[Firestore] ${context} kept locally:`, error?.code || error?.message);
    return false;
  }
}

/**
 * The address that always has access, matching `ownerEmail()` in
 * firestore.rules. Hardcoded in both places on purpose: an allowlist that lives
 * only in the database can be emptied, and then nobody can get back in to fix it.
 */
export const OWNER_EMAIL = 'ghoshsoumyabrata70@gmail.com';

/**
 * Does this signed-in user actually have admin rights?
 *
 * Mirrors `isAdmin()` in firestore.rules so the console can say so up front
 * instead of letting the operator find out when a save fails. The rules remain
 * the real enforcement — this is only what the UI shows.
 */
async function resolveAdminAccess(fb, user) {
  const email = (user?.email || '').trim().toLowerCase();
  if (email && email === OWNER_EMAIL) return true;

  try {
    const tokenResult = await user?.getIdTokenResult?.();
    if (tokenResult?.claims?.admin) return true;
    if (tokenResult?.claims?.email === OWNER_EMAIL) return true;
  } catch {
    /* fallback to doc check below */
  }

  if (!email) return false;

  try {
    const adminDoc = await fb.getDoc(fb.doc(fb.db, 'admins', email));
    return adminDoc.exists();
  } catch {
    return false;
  }
}


const ADMIN_WRITE_MESSAGES = {
  'permission-denied':
    'Firestore refused the change — this session is not an authorised admin. ' +
    'Sign out and back in, and check the address is listed under Settings → Administrators.',
  unauthenticated: 'Your admin session has expired. Sign in again and retry.',
  unavailable: 'Could not reach Firestore. Check your connection and try again.',
  'not-found': 'That record no longer exists in the database.',
};

/**
 * Admin writes, unlike lead capture, must never fail quietly.
 *
 * A lead held only in memory because Firestore was unreachable is still useful —
 * the contact is not lost. An admin who deletes a workshop and is told nothing
 * has been actively misled: the row vanishes optimistically, then the
 * `workshops` snapshot arrives with the document still present and puts it
 * straight back. Reporting the failure is the only honest option.
 */
async function adminWrite(operation, context) {
  try {
    await operation();
    return { ok: true };
  } catch (error) {
    const code = error?.code || 'unknown';
    console.warn(`[Firestore] ${context} failed:`, code, error?.message);
    return {
      ok: false,
      code,
      message: ADMIN_WRITE_MESSAGES[code] || error?.message || 'The change could not be saved.',
    };
  }
}

export const AppProvider = ({ children }) => {
  /* ── Routing ───────────────────────────────────────────────────────────── */
  const [route, setRoute] = useState(() =>
    typeof window === 'undefined' ? '/' : window.location.pathname
  );

  const navigate = useCallback((path) => {
    if (typeof window === 'undefined') return;
    window.history.pushState({}, '', path);
    setRoute(path);
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    const onPop = () => setRoute(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  /* ── Content ───────────────────────────────────────────────────────────── */
  const [workshops, setWorkshops] = useState(() => {
    // Read straight back, with no filtering.
    //
    // This used to drop any cached workshop whose id was one of the seed ids
    // (ws-101…ws-106), to stop a returning visitor seeing leftover placeholder
    // content. But this cache is only ever written from a live Firestore
    // snapshot — never from the seed file — so everything in it is real, and
    // the live catalogue is published *onto* those very ids by "Seed workshops
    // to Firestore". The filter therefore threw the real workshop away on
    // every cold load, and the page rendered its empty state until the first
    // snapshot arrived a second or two later.
    const cached = readStore('eduspire_workshops', []);
    return Array.isArray(cached)
      ? cached
          .filter(Boolean)
          .map((w) => (w.instructor === 'Ritu Malhotra' ? { ...w, instructor: 'Raja Mandal' } : w))
      : [];
  });
  const [registrations, setRegistrations] = useState(() => readStore('eduspire_registrations', []));
  const [leads, setLeads] = useState(() => readStore('eduspire_leads', []));
  const [testimonials, setTestimonials] = useState(() => readStore('eduspire_testimonials', []));
  const [adminSettings, setAdminSettings] = useState(() => {
    const cached = readStore('eduspire_admin_settings', null);
    if (!cached) return DEFAULT_ADMIN_SETTINGS;
    return {
      ...DEFAULT_ADMIN_SETTINGS,
      ...cached,
      supportWhatsapp: (!cached.supportWhatsapp || cached.supportWhatsapp === '+91 98765 43210' || cached.supportWhatsapp === '+919876543210')
        ? DEFAULT_ADMIN_SETTINGS.supportWhatsapp
        : cached.supportWhatsapp,
      supportEmail: (!cached.supportEmail || cached.supportEmail === 'contact@eduspire.in' || cached.supportEmail === 'rajamandal@eduspireoverseas.com')
        ? DEFAULT_ADMIN_SETTINGS.supportEmail
        : cached.supportEmail,
      companyAddress: (!cached.companyAddress || cached.companyAddress === 'Eduspire Tower, Sector V, Salt Lake, Kolkata, West Bengal 700091')
        ? DEFAULT_ADMIN_SETTINGS.companyAddress
        : cached.companyAddress,
      gstin: (!cached.gstin || cached.gstin === '19AAACE9921K1ZM')
        ? DEFAULT_ADMIN_SETTINGS.gstin
        : cached.gstin,
      cin: (!cached.cin || cached.cin === 'U74999WB2024PTC268491')
        ? DEFAULT_ADMIN_SETTINGS.cin
        : cached.cin,
    };
  });

  /* ── Session ───────────────────────────────────────────────────────────── */
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [currentRegistration, setCurrentRegistration] = useState(null);
  const [returnState, setReturnState] = useState(null); // 'checking' | 'failed' | null

  /* ── Auth ──────────────────────────────────────────────────────────────── */
  const [currentUser, setCurrentUser] = useState(() => readStore('eduspire_admin_session', null));
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(() => Boolean(readStore('eduspire_admin_session', null)));
  // Deliberately false until the Firebase auth observer confirms a real signed-in
  // user: a remembered session in localStorage proves nothing to Firestore.
  const [hasFirestoreAccess, setHasFirestoreAccess] = useState(false);
  /**
   * Why write access is missing, when it is.
   *
   * `hasFirestoreAccess: false` has two completely different causes and the
   * console used to report both as "this address is not an administrator" —
   * which is a lie when the address is the hardcoded owner and the real problem
   * is that the server never minted a Firebase session for it. Telling an owner
   * they are not an admin sends them looking in the one place that cannot help.
   *
   *   'not-admin'  — a real Firebase session, but the address has no grant.
   *   'no-session' — the code was correct and no Firebase session exists, so
   *                  firestore.rules sees no `request.auth` at all.
   */
  const [firestoreAccessIssue, setFirestoreAccessIssue] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  /* ── Sync ──────────────────────────────────────────────────────────────── */
  const [firestoreStatus, setFirestoreStatus] = useState('syncing');
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  /**
   * Where the catalogue currently on screen actually came from.
   *
   *   'firestore' — real rows from the database.
   *   'empty'     — database reachable but has no workshops; the seed samples
   *                 are being shown as a placeholder.
   *   'cache'     — database unreachable; showing localStorage or the seed.
   *
   * The public site is happy with any of these (never show an ad visitor a
   * blank page). The admin console must not present samples as real data, so
   * it reads this and says so.
   */
  const [catalogueSource, setCatalogueSource] = useState('cache');

  /* ── Attribution ───────────────────────────────────────────────────────── */
  const [attribution, setAttribution] = useState({});

  // Whether the payment API is running its simulator. Read once at boot so
  // records created during a test run can be tagged at the point of creation.
  const [paymentTestMode, setPaymentTestMode] = useState(false);

  // Guards the payment-return handler against StrictMode's double-invoke.
  const returnHandled = useRef(false);

  /* ═══════════════════════════════════════════════════════════════════════
     Boot: capture ad attribution, then start the pixel.
     Order matters — the pixel's first PageView should carry the click id.
     ═══════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    setAttribution(captureAttribution());
    initPixel();
    trackPageView();

    // Best-effort: if the API is unreachable we simply assume live mode.
    getJson('/api/health')
      .then((health) => setPaymentTestMode(Boolean(health.testMode)))
      .catch(() => setPaymentTestMode(false));

  }, []);

  /* The admin console is a modal rather than a page, but it is addressable at
     /admin so it can be bookmarked and linked. Driving it from `route` rather
     than from a mount-time check means in-app navigation opens it too, not
     only a cold hit on the URL. */
  useEffect(() => {
    if (route === '/admin') setActiveModal('admin');
  }, [route]);

  /* ═══════════════════════════════════════════════════════════════════════
     Firebase auth observer.
     ═══════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;

    (async () => {
      const fb = await loadFirebase();
      if (cancelled) return;

      unsubscribe = fb.onAuthStateChanged(fb.auth, async (user) => {
        setAuthLoading(false);

        if (user) {
          setCurrentUser(user);
          setIsAdminLoggedIn(true);
          // Authorisation is by email, matching firestore.rules. Signing in
          // proves who you are; this decides whether you may do anything.
          const allowed = await resolveAdminAccess(fb, user);
          setHasFirestoreAccess(allowed);
          setFirestoreAccessIssue(allowed ? null : { reason: 'not-admin', detail: '' });
        } else {
          setCurrentUser(null);
          setIsAdminLoggedIn(false);
          setHasFirestoreAccess(false);
          setFirestoreAccessIssue(null);
          writeStore('eduspire_admin_session', null);
        }
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  /* ═══════════════════════════════════════════════════════════════════════
     Live workshop catalogue (public read).
     ═══════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;

    (async () => {
      try {
        const fb = await loadFirebase();
        if (cancelled) return;

        unsubscribe = fb.onSnapshot(
          fb.collection(fb.db, 'workshops'),
          (snapshot) => {
            if (!snapshot.empty) {
              const remote = snapshot.docs.map((d) => {
                const data = d.data();
                return {
                  id: d.id,
                  ...data,
                  instructor: data.instructor === 'Ritu Malhotra' ? 'Raja Mandal' : data.instructor,
                };
              });
              setWorkshops(remote);
              writeStore('eduspire_workshops', remote);
              setCatalogueSource('firestore');
            } else {
              setWorkshops([]);
              writeStore('eduspire_workshops', []);
              setCatalogueSource('empty');
            }
            setFirestoreStatus('connected');
            setLastSyncedAt(new Date());
          },
          () => {
            setFirestoreStatus('disconnected');
            setCatalogueSource('cache');
          }
        );
      } catch {
        setFirestoreStatus('disconnected');
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  /* ═══════════════════════════════════════════════════════════════════════
     Live Site Settings (public read).
     ═══════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;

    (async () => {
      try {
        const fb = await loadFirebase();
        if (cancelled) return;

        unsubscribe = fb.onSnapshot(
          fb.doc(fb.db, 'settings', 'config'),
          (docSnap) => {
            if (docSnap.exists()) {
              const remote = docSnap.data();
              setAdminSettings((prev) => ({ ...prev, ...remote }));
              writeStore('eduspire_admin_settings', { ...DEFAULT_ADMIN_SETTINGS, ...remote });
            }
          },
          () => {}
        );
      } catch {
        /* no-op */
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);


  /* ═══════════════════════════════════════════════════════════════════════
     Testimonials (public read) — social proof on the landing page.
     ═══════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;

    (async () => {
      try {
        const fb = await loadFirebase();
        if (cancelled) return;

        unsubscribe = fb.onSnapshot(
          fb.collection(fb.db, 'testimonials'),
          (snapshot) => {
            const rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
            rows.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            setTestimonials(rows);
          },
          () => {
            /* Not fatal — the page falls back to written testimonials. */
          }
        );
      } catch {
        /* no-op */
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  /* ═══════════════════════════════════════════════════════════════════════
     Registrations and leads — admin-only reads.
     Subscribed lazily so a normal visitor never triggers a denied read.
     ═══════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (!hasFirestoreAccess) return undefined;

    let unsubscribers = [];
    let cancelled = false;

    (async () => {
      const fb = await loadFirebase();
      if (cancelled) return;

      const subscribe = (name, setter, storeKey) =>
        fb.onSnapshot(
          fb.collection(fb.db, name),
          (snapshot) => {
            const rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
            rows.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
            setter(rows);
            writeStore(storeKey, rows);
            setLastSyncedAt(new Date());
          },
          (error) => console.warn(`[Firestore] ${name} subscription failed:`, error.code)
        );

      unsubscribers = [
        subscribe('registrations', setRegistrations, 'eduspire_registrations'),
        subscribe('leadLanding', setLeads, 'eduspire_leads'),
      ];
    })();

    return () => {
      cancelled = true;
      unsubscribers.forEach((fn) => fn());
    };
  }, [hasFirestoreAccess]);

  /* ── Mirror everything to localStorage ──────────────────────────────────
     Not just on the Firestore snapshot: a lead captured while the database is
     unreachable exists only in memory until it is written here, and losing a
     contact you paid an ad click to acquire is the worst possible failure. */
  useEffect(() => writeStore('eduspire_admin_settings', adminSettings), [adminSettings]);
  useEffect(() => writeStore('eduspire_leads', leads), [leads]);
  useEffect(() => writeStore('eduspire_testimonials', testimonials), [testimonials]);
  useEffect(() => writeStore('eduspire_registrations', registrations), [registrations]);

  /* ── Lock background scroll while a modal is open ───────────────────────── */
  useEffect(() => {
    document.body.classList.toggle('body-locked', Boolean(activeModal));
    return () => document.body.classList.remove('body-locked');
  }, [activeModal]);

  /* ═══════════════════════════════════════════════════════════════════════
     Lead capture.

     Called the moment someone hands over contact details — before payment.
     On a paid campaign this is the whole point: a visitor who fills the form
     and abandons at checkout is still a lead you paid for and can follow up
     with, so the record has to exist before they leave for the gateway.
     ═══════════════════════════════════════════════════════════════════════ */
  const captureLead = useCallback(async (details, workshop, source = 'registration_form') => {
    const lead = {
      id: randomId('lead'),
      name: String(details.studentName || details.name || '').slice(0, 100),
      phone: String(details.phone || '').slice(0, 20),
      email: String(details.email || '').slice(0, 120),
      city: details.city || '',
      qualification: details.qualification || '',
      profession: details.profession || '',
      workshopId: workshop?.id || null,
      workshopTitle: workshop?.title || null,
      interestValue: workshop?.price || 0,
      source,
      status: 'NEW',
      converted: false,
      isTest: paymentTestMode,
      createdAt: new Date().toISOString(),
      ...attributionForRecord(),
    };

    setLeads((prev) => [lead, ...prev]);

    const fb = await loadFirebase();
    await tryWrite(() => fb.setDoc(fb.doc(fb.db, 'leadLanding', lead.id), lead), 'leadLanding');

    trackLead(workshop, {
      email: lead.email,
      phone: lead.phone,
      name: lead.name,
      city: lead.city,
    });

    return lead;
  }, [paymentTestMode]);

  /* ═══════════════════════════════════════════════════════════════════════
     Registration → checkout.
     ═══════════════════════════════════════════════════════════════════════ */
  const openRegistration = useCallback((workshop) => {
    setSelectedWorkshop(workshop);
    setActiveModal('registration');
    trackViewContent(workshop);
  }, []);

  const startCheckout = useCallback(
    async (studentData) => {
      if (!selectedWorkshop) return null;

      const { net, gst, total } = priceBreakdown(studentData.amount ?? selectedWorkshop.price, 0);

      // Cashfree's order_id allows only [A-Za-z0-9_-], 3–45 characters.
      const registration = {
        id: `EDU${Date.now().toString(36).toUpperCase()}${Math.floor(Math.random() * 900 + 100)}`,
        studentName: studentData.studentName,
        email: studentData.email,
        phone: studentData.phone,
        city: studentData.city || '',
        qualification: studentData.qualification || '',
        profession: studentData.profession || '',
        workshopId: selectedWorkshop.id,
        workshopTitle: selectedWorkshop.title,
        // Copied onto the order rather than looked up later: the receipt must
        // still read correctly if the workshop is edited or removed afterwards.
        workshopDate: selectedWorkshop.date || null,
        workshopTime: selectedWorkshop.time || null,
        whatsappLink: selectedWorkshop.whatsappLink,
        amount: net,
        gst,
        totalPaid: total,
        couponCode: studentData.couponCode || null,
        discount: studentData.discount || 0,
        status: 'PENDING',
        createdAt: new Date().toISOString(),
        date: formatDateTime(new Date()),
        ...attributionForRecord(),
      };

      // Persist the lead first: if anything downstream fails we still have the
      // contact details we paid to acquire.
      await captureLead(studentData, selectedWorkshop);

      const fb = await loadFirebase();
      await tryWrite(
        () => fb.setDoc(fb.doc(fb.db, 'registrations', registration.id), registration),
        'registration'
      );

      setRegistrations((prev) => [registration, ...prev.filter((r) => r.id !== registration.id)]);
      setCurrentRegistration(registration);

      // Remember which order we sent the customer away with, so the return
      // handler can pick the flow back up after the gateway redirect.
      writeStore('eduspire_pending_order', registration);

      trackInitiateCheckout(registration);
      setActiveModal('checkout');
      return registration;
    },
    [selectedWorkshop, captureLead]
  );

  /* ═══════════════════════════════════════════════════════════════════════
     Payment confirmed.

     Only ever called after the server has verified the state with Cashfree —
     the browser never decides on its own that a payment succeeded.
     ═══════════════════════════════════════════════════════════════════════ */
  const completePaymentSuccess = useCallback(
    async (paymentDetails = {}, registrationOverride = null) => {
      const base = registrationOverride || currentRegistration;
      if (!base) return null;

      const completed = {
        ...base,
        status: 'SUCCESS',
        txnId: paymentDetails.transactionId || base.txnId || `TXN_${base.id}`,
        // Renamed from `phonePeRef` when the gateway changed. Records written
        // before that still carry the old key, so everything that displays this
        // falls back to it rather than showing a blank reference.
        paymentRef: paymentDetails.paymentRef || paymentDetails.orderId || base.paymentRef || base.id,
        paymentMode: paymentDetails.paymentMode || 'Cashfree Checkout',
        paidAt: new Date().toISOString(),
        whatsappSent: true,
        // Tagged at the source so simulated payments can be filtered out of
        // revenue rather than quietly inflating it.
        isTest: Boolean(paymentDetails.isTest),

        // Issued by the server the moment the payment was confirmed — the
        // number from a shared Firestore counter, the group link from the
        // workshop document. Both are displayed, neither is decided here: an
        // invoice number a browser made up would collide with a real one, and
        // a group link it remembered could be one an admin has since replaced.
        invoiceNumber: paymentDetails.invoiceNumber || base.invoiceNumber || null,
        whatsappLink: paymentDetails.whatsappLink || base.whatsappLink || null,
        invoiceEmailed: Boolean(paymentDetails.invoiceEmailed),
        invoiceEmailedTo: paymentDetails.invoiceEmailedTo || base.email || null,
      };

      const fb = await loadFirebase();

      // Expected to fail for anonymous visitors: the rules deliberately forbid
      // the browser from marking its own payment successful. The authoritative
      // record is written server-side the moment Cashfree confirms the payment
      // — see fulfilOrder() in functions/fulfilment.js, which also issues the
      // invoice and sends it on. This attempt is kept only so an *admin* taking
      // a booking in the console sees the row update without a reload.
      await tryWrite(
        () =>
          fb.updateDoc(fb.doc(fb.db, 'registrations', completed.id), {
            status: 'SUCCESS',
            txnId: completed.txnId,
            paymentRef: completed.paymentRef,
            paymentMode: completed.paymentMode,
            paidAt: completed.paidAt,
            whatsappSent: true,
            // Must be persisted, not just held in local state — the admin
            // console reads Firestore, and an untagged simulated payment shows
            // up there as real revenue.
            isTest: completed.isTest,
          }),
        'payment confirmation'
      );

      setRegistrations((prev) =>
        prev.some((r) => r.id === completed.id)
          ? prev.map((r) => (r.id === completed.id ? completed : r))
          : [completed, ...prev]
      );
      setCurrentRegistration(completed);

      // Show the seat gone immediately. The authoritative decrement happens on
      // the server — `workshops` is admin-only writable, so the browser could
      // not persist this even if it should — and the onSnapshot subscription
      // above replaces this value with the stored one as soon as it lands.
      setWorkshops((prev) =>
        prev.map((w) =>
          w.id === completed.workshopId ? { ...w, seatsLeft: Math.max(0, (w.seatsLeft ?? 1) - 1) } : w
        )
      );

      // Update or create lead status in leadLanding Firestore collection
      const targetLead = leads.find(
        (l) => l.phone === completed.phone && l.workshopId === completed.workshopId
      );
      if (targetLead) {
        await tryWrite(
          () =>
            fb.updateDoc(fb.doc(fb.db, 'leadLanding', targetLead.id), {
              converted: true,
              status: 'CONVERTED',
            }),
          'lead status update'
        );
      } else {
        await captureLead(
          {
            studentName: completed.studentName,
            phone: completed.phone,
            email: completed.email,
            city: completed.city,
            qualification: completed.qualification,
            profession: completed.profession,
          },
          { id: completed.workshopId, title: completed.workshopTitle, price: completed.amount },
          'paid_registration'
        );
      }

      setLeads((prev) =>
        prev.map((l) =>
          l.phone === completed.phone && l.workshopId === completed.workshopId
            ? { ...l, converted: true, status: 'CONVERTED' }
            : l
        )
      );

      trackPurchase(completed);

      try {
        localStorage.removeItem('eduspire_pending_order');
      } catch {
        /* no-op */
      }

      setActiveModal('receipt');
      return completed;
    },
    [currentRegistration]
  );

  /* ═══════════════════════════════════════════════════════════════════════
     Return from the gateway.

     Cashfree sends the customer back to `/?payment=return&orderId=…`. The query
     string proves nothing on its own, so we re-check the real state with our
     server before showing a receipt.
     ═══════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (returnHandled.current) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') !== 'return') return;

    returnHandled.current = true;
    const orderId = params.get('orderId');

    // Clean the params out of the address bar straight away.
    window.history.replaceState({}, '', window.location.pathname);

    if (!orderId) return;

    const pending = readStore('eduspire_pending_order', null);
    setReturnState('checking');

    (async () => {
      // A bank redirect can take a beat to settle; retry briefly before giving up.
      for (let attempt = 0; attempt < 8; attempt += 1) {
        try {
          const result = await fetchPaymentStatus(orderId);
          const state = String(result.state || '').toUpperCase();

          if (state === 'COMPLETED' || state === 'SUCCESS') {
            setReturnState(null);
            await completePaymentSuccess(
              {
                orderId: result.orderId,
                transactionId: result.transactionId,
                paymentMode: result.paymentMode,
                isTest: Boolean(result.testMode),
                invoiceNumber: result.invoiceNumber,
                whatsappLink: result.whatsappLink,
                invoiceEmailed: result.invoiceEmailed,
                invoiceEmailedTo: result.invoiceEmailedTo,
              },
              pending
            );
            return;
          }

          if (['FAILED', 'DECLINED', 'ERROR', 'CANCELLED'].includes(state)) {
            setReturnState('failed');
            return;
          }
        } catch {
          /* Transient — fall through to the retry delay. */
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      setReturnState('failed');
    })();
    // Runs once on mount; completePaymentSuccess is captured deliberately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeModal = useCallback(() => {
    setActiveModal(null);
    setReturnState(null);

    // Leaving the console should leave its URL behind as well, or the next
    // render re-opens it. Replaced rather than pushed, so Back does not drop
    // the operator straight back into the panel they just closed.
    if (typeof window !== 'undefined' && window.location.pathname === '/admin') {
      window.history.replaceState({}, '', '/');
      setRoute('/');
    }
  }, []);

  /* ═══════════════════════════════════════════════════════════════════════
     Admin authentication — emailed one-time code.

     Two things have to happen and only one of them is the code. Verifying it
     proves who the operator is to *our* API; exchanging the Firebase custom
     token that comes back is what gives firestore.rules a real `request.auth`.
     Skip the exchange and the console looks signed in while every save is
     silently rejected — which is exactly how deleting a workshop appeared to
     do nothing.
     ═══════════════════════════════════════════════════════════════════════ */
  const handleSendOtp = useCallback(async (email) => {
    try {
      const res = await requestLoginOtp(String(email || '').trim().toLowerCase());
      // Deliberately does not pass through any code the API might return. The
      // code belongs in the email and nowhere else — surfacing it here would
      // put it one render away from being shown on screen.
      return { success: true, message: res.message };
    } catch (error) {
      return { success: false, error: error.message || 'Could not send the verification code.' };
    }
  }, []);

  const handleVerifyOtp = useCallback(async (email, otp) => {
    try {
      const res = await verifyLoginOtp(String(email || '').trim().toLowerCase(), otp);
      if (!res.success || !res.user) return { success: false, error: 'Verification failed.' };

      const userObj = {
        email: res.user.email,
        uid: res.user.uid || null,
        authenticatedAt: res.user.authenticatedAt,
      };

      let writeAccess = false;
      let warning = '';

      if (res.firebaseToken) {
        try {
          const fb = await loadFirebase();
          const credential = await fb.signInWithCustomToken(fb.auth, res.firebaseToken);
          userObj.uid = credential.user.uid;
          writeAccess = await resolveAdminAccess(fb, credential.user);
          if (!writeAccess) {
            warning =
              `Signed in as ${credential.user.email}, but that address is not an administrator. ` +
              'Ask an existing admin to add it under Settings → Administrators.';
          }
        } catch (error) {
          warning =
            'Your code was correct, but the Firebase session could not be established ' +
            `(${error?.code || 'unknown'}). Nothing you change here will save.`;
        }
      } else {
        warning = res.firebaseTokenError
          ? `Your code was correct, but the server could not issue a Firebase session: ${res.firebaseTokenError}`
          : 'Your code was correct, but the server has no Firebase service account configured, ' +
            'so nothing you change here will save.';
      }

      // The auth observer sets these too once the custom-token sign-in lands.
      // Setting them here as well means the console opens immediately.
      setCurrentUser(userObj);
      setIsAdminLoggedIn(true);
      setHasFirestoreAccess(writeAccess);
      setFirestoreAccessIssue(
        writeAccess
          ? null
          : {
              // No token at all is a server configuration problem, not a
              // permissions one — keep the two apart for the banner.
              reason: res.firebaseToken ? 'not-admin' : 'no-session',
              detail: res.firebaseTokenError || '',
            }
      );
      writeStore('eduspire_admin_session', userObj);

      return { success: true, user: userObj, writeAccess, warning };
    } catch (error) {
      return { success: false, error: error.message || 'Could not verify that code.' };
    }
  }, []);

  const handleFirebaseEmailLogin = useCallback(async (email, password) => {
    try {
      const fb = await loadFirebase();
      const credential = await fb.signInWithEmailAndPassword(fb.auth, email, password);
      return { success: true, user: credential.user };
    } catch (error) {
      const messages = {
        'auth/invalid-credential': 'That email and password combination is not recognised.',
        'auth/invalid-email': 'That does not look like a valid email address.',
        'auth/user-not-found': 'No admin account exists for that email.',
        'auth/wrong-password': 'Incorrect password.',
        'auth/too-many-requests': 'Too many attempts. Please wait a few minutes.',
        'auth/network-request-failed': 'Network error — check your connection.',
      };
      return { success: false, error: messages[error.code] || 'Sign-in failed. Please try again.' };
    }
  }, []);

  const handleFirebaseLogout = useCallback(async () => {
    try {
      const fb = await loadFirebase();
      await fb.signOut(fb.auth);
    } catch {
      /* Sign out locally regardless. */
    }
    writeStore('eduspire_admin_session', null);
    setCurrentUser(null);
    setIsAdminLoggedIn(false);
    setHasFirestoreAccess(false);
    setFirestoreAccessIssue(null);
  }, []);

  /* ═══════════════════════════════════════════════════════════════════════
     Administrators.

     A grant is simply a document at `admins/<lowercased email>`. The person
     then signs in with a link sent to that address and firestore.rules lets
     them through — no Firebase console, no uid to look up, no password to
     issue. The owner is enforced in the rules and cannot be removed here.
     ═══════════════════════════════════════════════════════════════════════ */
  const listAdmins = useCallback(async () => {
    try {
      const fb = await loadFirebase();
      const snapshot = await fb.getDocs(fb.collection(fb.db, 'admins'));
      const rows = snapshot.docs
        .map((d) => ({ email: d.id, ...d.data() }))
        // Legacy grants were keyed by uid rather than email and can never match
        // a signed-in address, so they would only be confusing to display.
        .filter((row) => row.email.includes('@'));

      if (!rows.some((row) => row.email === OWNER_EMAIL)) {
        rows.unshift({ email: OWNER_EMAIL, isOwner: true });
      }

      return {
        ok: true,
        admins: rows
          .map((row) => ({ ...row, isOwner: row.email === OWNER_EMAIL }))
          .sort((a, b) => Number(b.isOwner) - Number(a.isOwner) || a.email.localeCompare(b.email)),
      };
    } catch (error) {
      return { ok: false, admins: [], message: ADMIN_WRITE_MESSAGES[error?.code] || 'Could not load administrators.' };
    }
  }, []);

  const addAdmin = useCallback(async (email) => {
    const clean = String(email || '').trim().toLowerCase();
    if (!/^[^@\s/]+@[^@\s/]+\.[^@\s/]+$/.test(clean)) {
      return { ok: false, message: 'Enter a valid email address.' };
    }

    const fb = await loadFirebase();
    return adminWrite(
      () =>
        fb.setDoc(fb.doc(fb.db, 'admins', clean), {
          email: clean,
          grantedBy: currentUser?.email || 'unknown',
          grantedAt: new Date().toISOString(),
        }),
      'add administrator'
    );
  }, [currentUser]);

  const removeAdmin = useCallback(async (email) => {
    const clean = String(email || '').trim().toLowerCase();
    if (clean === OWNER_EMAIL) {
      return { ok: false, message: 'The owner account cannot be removed.' };
    }

    const fb = await loadFirebase();
    return adminWrite(() => fb.deleteDoc(fb.doc(fb.db, 'admins', clean)), 'remove administrator');
  }, []);

  /* ═══════════════════════════════════════════════════════════════════════
     Workshop CRUD (admin)
     ═══════════════════════════════════════════════════════════════════════ */
  const addWorkshop = useCallback(async (data) => {
    const created = {
      ...data,
      id: `ws-${Date.now().toString(36)}`,
      seatsLeft: Number(data.maxSeats) || 50,
      rating: Number(data.rating) || 5,
      reviewsCount: Number(data.reviewsCount) || 0,
      createdAt: new Date().toISOString(),
    };

    const fb = await loadFirebase();
    const result = await adminWrite(
      () => fb.setDoc(fb.doc(fb.db, 'workshops', created.id), created),
      'workshop create'
    );

    // Only show it once the database has it. Adding it locally first means a
    // rejected write leaves a workshop on screen that visitors will never see.
    if (result.ok) setWorkshops((prev) => [created, ...prev]);

    return { ...result, workshop: created };
  }, []);

  const updateWorkshop = useCallback(async (id, changes) => {
    const previous = workshops.find((w) => w.id === id);
    setWorkshops((prev) => prev.map((w) => (w.id === id ? { ...w, ...changes } : w)));

    const fb = await loadFirebase();
    const result = await adminWrite(
      () => fb.updateDoc(fb.doc(fb.db, 'workshops', id), changes),
      'workshop update'
    );

    // Put the old values back rather than leaving edits on screen that were
    // never saved — the next snapshot would revert them anyway, seconds later.
    if (!result.ok && previous) {
      setWorkshops((prev) => prev.map((w) => (w.id === id ? previous : w)));
    }

    return result;
  }, [workshops]);

  /**
   * Delete every simulated payment.
   *
   * Test runs create real Firestore documents that look exactly like real
   * enrolments apart from the `isTest` flag. This removes them so the console
   * shows only genuine students.
   */
  const purgeTestData = useCallback(async () => {
    const doomedRegs = registrations.filter((r) => r.isTest);
    const doomedLeads = leads.filter((l) => l.isTest);
    const attempted = doomedRegs.length + doomedLeads.length;
    if (!attempted) return { removed: 0, attempted: 0 };

    const fb = await loadFirebase();
    let removed = 0;

    const wipe = async (collectionName, rows) => {
      for (const row of rows) {
        const ok = await tryWrite(
          () => fb.deleteDoc(fb.doc(fb.db, collectionName, row.id)),
          `test ${collectionName} delete`
        );
        if (ok) removed += 1;
      }
    };

    await wipe('registrations', doomedRegs);
    await wipe('leadLanding', doomedLeads);

    setRegistrations((prev) => prev.filter((r) => !r.isTest));
    setLeads((prev) => prev.filter((l) => !l.isTest));
    return { removed, attempted };
  }, [registrations, leads]);

  /* ═══════════════════════════════════════════════════════════════════════
     Testimonial videos
     ═══════════════════════════════════════════════════════════════════════ */

  /**
   * Upload a testimonial video to Firebase Storage.
   *
   * Videos cannot live in Firestore — a document is capped at 1MB and a phone
   * recording is tens of megabytes. The file goes to Storage and only the
   * resulting download URL is stored on the testimonial document.
   *
   * @param {File} file
   * @param {(percent: number) => void} onProgress
   */
  const uploadTestimonialVideo = useCallback(async (file, onProgress) => {
    if (!file) throw new Error('No file selected.');

    if (!file.type.startsWith('video/')) {
      throw new Error('That is not a video file. Upload an MP4, MOV or WebM.');
    }

    if (file.size > MAX_VIDEO_BYTES) {
      const mb = Math.round(file.size / 1024 / 1024);
      throw new Error(`Video is ${mb}MB — the limit is 100MB. Compress it or trim it down.`);
    }

    const fb = await loadFirebase();
    // Keep the extension so the browser can pick the right decoder.
    const extension = (file.name.split('.').pop() || 'mp4').toLowerCase().slice(0, 5);
    const path = `testimonial-videos/${randomId('vid')}.${extension}`;
    const ref = fb.storageRef(fb.storage, path);

    const task = fb.uploadBytesResumable(ref, file, { contentType: file.type });

    await new Promise((resolve, reject) => {
      task.on(
        'state_changed',
        (snapshot) => {
          const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          onProgress?.(percent);
        },
        (error) => {
          const messages = {
            'storage/unauthorized':
              'Storage rejected the upload. Deploy storage.rules and sign in as an admin.',
            'storage/canceled': 'Upload cancelled.',
            'storage/retry-limit-exceeded': 'Upload timed out. Check your connection and retry.',
          };
          reject(new Error(messages[error.code] || `Upload failed (${error.code || 'unknown'}).`));
        },
        resolve
      );
    });

    const url = await fb.getDownloadURL(ref);
    return { url, path };
  }, []);

  /**
   * List all video files currently uploaded in Firebase Storage under `testimonial-videos`.
   */
  const fetchStorageTestimonialVideos = useCallback(async () => {
    const fb = await loadFirebase();
    const folderRef = fb.storageRef(fb.storage, 'testimonial-videos');
    const result = await fb.listAll(folderRef);

    const items = await Promise.all(
      result.items.map(async (itemRef) => {
        const [url, metadata] = await Promise.all([
          fb.getDownloadURL(itemRef).catch(() => ''),
          fb.getMetadata(itemRef).catch(() => ({})),
        ]);
        return {
          name: itemRef.name,
          fullPath: itemRef.fullPath,
          url,
          size: metadata.size || 0,
          contentType: metadata.contentType || 'video/mp4',
          timeCreated: metadata.timeCreated || '',
          updated: metadata.updated || '',
        };
      })
    );

    return items.filter((item) => Boolean(item.url));
  }, []);

  /**
   * Sync video files found in Firebase Storage `testimonial-videos/` into Firestore testimonials.
   */
  const syncTestimonialsFromStorage = useCallback(async () => {
    const files = await fetchStorageTestimonialVideos();
    if (!files.length) return { added: 0, total: 0 };

    const fb = await loadFirebase();
    let addedCount = 0;

    for (const file of files) {
      const alreadyExists = testimonials.some(
        (t) => t.videoUrl === file.url || (t.storagePath && t.storagePath === file.fullPath)
      );

      if (!alreadyExists) {
        const cleanName = file.name
          .replace(/\.[^.]+$/, '')
          .replace(/[_-]+/g, ' ')
          .trim();

        const docId = randomId('tst');
        const record = {
          id: docId,
          studentName: cleanName || 'Student Testimonial',
          role: '',
          workshopId: '',
          workshopTitle: '',
          batchLabel: 'Live Class Batch',
          classDate: '',
          quote: '',
          rating: 5,
          videoUrl: file.url,
          storagePath: file.fullPath,
          videoType: 'upload',
          posterUrl: '',
          published: true,
          order: testimonials.length + addedCount,
          createdAt: file.timeCreated || new Date().toISOString(),
        };

        await tryWrite(() => fb.setDoc(fb.doc(fb.db, 'testimonials', docId), record), 'sync testimonial');
        addedCount += 1;
      }
    }

    return { added: addedCount, total: files.length };
  }, [fetchStorageTestimonialVideos, testimonials]);

  const saveTestimonial = useCallback(async (testimonial) => {
    const record = {
      order: testimonials.length,
      published: true,
      createdAt: new Date().toISOString(),
      ...testimonial,
      id: testimonial.id || randomId('tst'),
    };

    setTestimonials((prev) => {
      const exists = prev.some((t) => t.id === record.id);
      return exists ? prev.map((t) => (t.id === record.id ? record : t)) : [...prev, record];
    });

    const fb = await loadFirebase();
    await tryWrite(() => fb.setDoc(fb.doc(fb.db, 'testimonials', record.id), record), 'testimonial');

    return record;
  }, [testimonials.length]);

  const deleteTestimonial = useCallback(async (testimonial) => {
    setTestimonials((prev) => prev.filter((t) => t.id !== testimonial.id));

    const fb = await loadFirebase();
    await tryWrite(
      () => fb.deleteDoc(fb.doc(fb.db, 'testimonials', testimonial.id)),
      'testimonial delete'
    );

    // Remove the underlying file too, or the bucket fills with orphans you
    // still pay to store.
    if (testimonial.storagePath) {
      await tryWrite(
        () => fb.deleteObject(fb.storageRef(fb.storage, testimonial.storagePath)),
        'testimonial video delete'
      );
    }
  }, []);

  /**
   * Delete a single enrolment or lead.
   *
   * Needed for records that predate the `isTest` flag, or any junk entry that
   * would otherwise sit in the console looking like a real student.
   */
  const deleteRegistration = useCallback(async (id) => {
    setRegistrations((prev) => prev.filter((r) => r.id !== id));
    const fb = await loadFirebase();
    return tryWrite(() => fb.deleteDoc(fb.doc(fb.db, 'registrations', id)), 'registration delete');
  }, []);

  const deleteLead = useCallback(async (id) => {
    setLeads((prev) => prev.filter((l) => l.id !== id));
    const fb = await loadFirebase();
    return tryWrite(() => fb.deleteDoc(fb.doc(fb.db, 'leadLanding', id)), 'leadLanding delete');
  }, []);

  /**
   * Remove a workshop from the catalogue.
   *
   * The delete has to reach Firestore *before* the row leaves the screen. The
   * `workshops` subscription is live, so an optimistic removal that the
   * database then rejects is undone the moment the next snapshot lands — the
   * workshop reappears and the operator is left thinking the button is broken.
   */
  const deleteWorkshop = useCallback(async (id) => {
    const fb = await loadFirebase();
    const result = await adminWrite(() => fb.deleteDoc(fb.doc(fb.db, 'workshops', id)), 'workshop delete');
    if (!result.ok) return result;

    const next = workshops.filter((w) => w.id !== id);
    setWorkshops(next);

    // Mirror immediately. Deleting the *last* workshop produces an empty
    // snapshot, and the subscription deliberately ignores those so the public
    // page can fall back to the seed catalogue — which would otherwise leave
    // the deleted row sitting in localStorage, back again on the next reload.
    writeStore('eduspire_workshops', next);

    return result;
  }, [workshops]);

  const updateAdminSettings = useCallback(async (newSettings) => {
    setAdminSettings(newSettings);
    writeStore('eduspire_admin_settings', newSettings);

    const fb = await loadFirebase();
    return adminWrite(
      () => fb.setDoc(fb.doc(fb.db, 'settings', 'config'), newSettings, { merge: true }),
      'settings update'
    );
  }, []);

  const value = {
    // routing
    route,
    navigate,

    // content
    workshops,
    registrations,
    leads,
    testimonials,
    adminSettings,
    setAdminSettings,
    updateAdminSettings,

    // session
    selectedWorkshop,
    setSelectedWorkshop,
    activeModal,
    setActiveModal,
    currentRegistration,
    setCurrentRegistration,
    returnState,

    // auth
    currentUser,
    isAdminLoggedIn,
    setIsAdminLoggedIn,
    hasFirestoreAccess,
    firestoreAccessIssue,
    authLoading,
    handleSendOtp,
    handleVerifyOtp,
    handleFirebaseEmailLogin,
    handleFirebaseLogout,
    listAdmins,
    addAdmin,
    removeAdmin,
    ownerEmail: OWNER_EMAIL,

    // sync
    firestoreStatus,
    lastSyncedAt,
    catalogueSource,
    attribution,
    purgeTestData,

    // actions
    openRegistration,
    startCheckout,
    completePaymentSuccess,
    captureLead,
    closeModal,
    addWorkshop,
    updateWorkshop,
    deleteWorkshop,
    deleteRegistration,
    deleteLead,
    uploadTestimonialVideo,
    fetchStorageTestimonialVideos,
    syncTestimonialsFromStorage,
    saveTestimonial,
    deleteTestimonial,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};


export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used inside <AppProvider>');
  return context;
};
